import React, { useCallback, useEffect, useState } from "react";
import { Modal, Input, message } from "antd";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppAvatarUrl } from "../../utils/resolveAppMediaUrl";
import type { ViewpointFeedPost } from "./ViewpointFeedTypes";
import { VIEWPOINT_REPORT_TYPES } from "./ViewpointReportTypes";
import { ViewpointReportApi } from "./ViewpointReportApi";
import "./ViewpointReportModal.css";

const MAX_NOTE = 100;

function displayPublishedCompact(v: unknown): string {
  if (v == null || v === "") return "";
  if (v instanceof Date) return dayjs(v).format("M月D日 HH:mm");
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format("M月D日 HH:mm") : t;
  }
  return String(v).trim();
}

function nicknameOf(row: ViewpointFeedPost): string {
  const n = row.publisherNickname?.trim();
  if (n) return n;
  return row.publisherLabel?.trim() || "用户";
}

type Props = {
  open: boolean;
  row: ViewpointFeedPost | null;
  spaceId: string;
  spaceProductId: string;
  onClose: () => void;
  onSubmitted?: () => void;
};

export function ViewpointReportModal({
  open,
  row,
  spaceId,
  spaceProductId,
  onClose,
  onSubmitted,
}: Props): JSX.Element | null {
  const [reportType, setReportType] = useState<string>("");
  const [detailNote, setDetailNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReportType("");
      setDetailNote("");
    }
  }, [open]);

  const nick = row ? nicknameOf(row) : "";
  const titleText = row?.titleMode && row.title?.trim() ? row.title.trim() : null;

  const onConfirm = useCallback(async () => {
    if (!row) return;
    const type = reportType.trim();
    if (!type) {
      message.warning("请选择举报类型");
      return;
    }
    setSubmitting(true);
    try {
      const res = await ViewpointReportApi.submit({
        spaceId,
        spaceProductId,
        reportTargetType: "viewpoint",
        targetId: row.id,
        reportType: type,
        detailNote: detailNote.trim(),
      });
      if (typeof res !== "string" || !res.trim()) {
        message.error("提交失败");
        return;
      }
      if (res.length < 4) {
        message.error(res);
        return;
      }
      message.success("举报已提交，感谢您的反馈。");
      onSubmitted?.();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }, [row, reportType, detailNote, spaceId, spaceProductId, onClose, onSubmitted]);

  if (!row) {
    return null;
  }

  return (
    <Modal
      open={open}
      title="举报"
      onCancel={onClose}
      destroyOnClose
      centered
      width="min(520px, calc(100vw - 32px))"
      className="lf-vp-report-modal"
      footer={
        <div className="lf-vp-report-modal__footer">
          <button type="button" className="lf-vp-report-btn lf-vp-report-btn--ghost" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="lf-vp-report-btn lf-vp-report-btn--primary"
            disabled={submitting}
            onClick={() => void onConfirm()}
          >
            确认举报
          </button>
        </div>
      }
    >
      <div className="lf-vp-report-modal__block">
        <div className="lf-vp-report-target">
          <EasyAvatar
            shape="square"
            squareRadius={4}
            size={28}
            url={resolveAppAvatarUrl(row.publisherAvatarUrl) || undefined}
            word={avatarDisplayWord(nick, row.publisherLabel)}
            alt=""
          />
          <div className="lf-vp-report-target__main">
            <div className="lf-vp-report-target__head">
              <span className="lf-vp-report-target__nick">{nick}</span>
              <span className="lf-vp-report-target__time">
                {displayPublishedCompact(row.publishedAt)}
              </span>
            </div>
            {titleText ? <div className="lf-vp-report-target__title">{titleText}</div> : null}
            <div className="lf-vp-report-target__body">{row.content}</div>
          </div>
        </div>
      </div>
      <div className="lf-vp-report-modal__block">
        <div className="lf-vp-report-modal__label">举报类型</div>
        <div className="lf-vp-report-type-grid">
          {VIEWPOINT_REPORT_TYPES.map((t) => {
            const on = reportType === t.code;
            return (
              <button
                key={t.code}
                type="button"
                className={on ? "lf-vp-report-type lf-vp-report-type--on" : "lf-vp-report-type"}
                onClick={() => setReportType(t.code)}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="lf-vp-report-modal__block">
        <div className="lf-vp-report-modal__label">详细说明</div>
        <div className="lf-vp-report-textarea-wrap">
          <Input.TextArea
            className="lf-vp-report-textarea"
            placeholder="选填"
            value={detailNote}
            maxLength={MAX_NOTE}
            rows={3}
            onChange={(e) => setDetailNote(e.target.value)}
          />
          <span className="lf-vp-report-textarea__count" aria-hidden>
            {detailNote.length}/{MAX_NOTE}
          </span>
        </div>
      </div>
    </Modal>
  );
}
